import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { DynamicBuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomKind } from "building-types/rooms/roomTypesEnum";
import type { WallPlaneDomainOperation } from "building-types/walls2/wallPlane";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Face } from "geom/brep";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { WallAssembly } from "./wallAssemblyTypes";
import type { WallCut } from "./wallCutTypes";
import type { WallDescriptor, WallSection } from "./wallDesciptorTypes";
import type { WallSideDescriptor } from "./wallDesciptorTypes";
import type { WallEndCap, HalfEdgeOffsets } from "./wallEndsTypes";
import type { WallLayerType, WallType } from "./wallEnums";
import type { WallFaces } from "./wallFacesTypes";
import type { WallSidePathDomain } from "./wallSideTypes";
import type { WallCondition } from "./wallType";

/**
 * Represents a 2D polygon intersection between a wall footprint and foundation
 * plan profiles at a specific Z range.
 */
export interface WallFoundationIntersection {
  readonly profiles: PolyLine2Polygon[];
  readonly zMin: number;
  readonly zMax: number;
}

export interface WallMassBase {
  readonly leftEdge: PolyLine2; // wrt half edge, runs from end to start on left side
  readonly leftFace: Face;
  readonly rightEdge: PolyLine2; // wrt half edge, runs from start to end on right side
  readonly rightFace: Face;
  readonly start: WallEndCap;
  readonly startFace: Face | null;
  readonly end: WallEndCap;
  readonly endFace: Face | null;
  readonly bottomFace: Face;
  readonly topFace: Face;
  readonly footprint: PolyLine2;
  readonly thickness: number;
}

export interface WallPathBase {
  readonly wallType: WallType;
  /**
   * Room planar-graph half-edge for this wall path.
   * Freeform walls use {@link FREEFORM_WALL_HALF_EDGE_INDEX} and are not
   * present in `halfEdgeToWall`.
   */
  readonly halfEdgeIndex: number;
}

/**
 * Sentinel `WallPath.halfEdgeIndex` for freeform walls (not a room PPG index).
 * Freeform masses live only in `WallMassSystem.walls`, never in `halfEdgeToWall`.
 *
 * Brittleness note: much of the legacy wall system assumes `halfEdgeIndex` indexes
 * into the room planar graph. Freeform walls piggyback on that type with this
 * sentinel to minimize blast radius. Every consumer must guard before calling
 * `planarGraphAdjacentHalfEdge`, indexing `halfEdgeToWall`, or other PPG lookups.
 */
export const FREEFORM_WALL_HALF_EDGE_INDEX = -1;

export interface WallPath extends WallPathBase {
  readonly wallType: WallType;
  readonly wallCondition: WallCondition;
  readonly wallDescriptor: WallDescriptor;
  readonly halfEdgeIndex: number;
  readonly leftRoom: Room | undefined;
  readonly rightRoom: Room | undefined;
  // @TODO - move roomKind, roomSupport and roomCover into an object
  readonly leftRoomKind: RoomKind;
  readonly rightRoomKind: RoomKind;
  //
  readonly startX: number;
  readonly startY: number;
  readonly endX: number;
  readonly endY: number;
  readonly length: number;
  readonly baseOffset: number; // TODO - deprecate
}

export type WallLayer = SolidWallLayer | VoidWallLayer;

interface WallBase<T extends WallType> extends HalfEdgeOffsets, DynamicBuildingEntityBase<"WALL"> {
  readonly wallType: T;
  readonly wallAssembly: WallAssembly;
  readonly path: WallPath;
  readonly section: WallSection;
  readonly floorElevation: number;
  readonly layers: WallLayer[];
  readonly leftDomain: WallSidePathDomain;
  readonly rightDomain: WallSidePathDomain;
  readonly roofClippingOperations?: WallPlaneDomainOperation[];
  readonly roofClippingOpsBySubMassId?: ReadonlyMap<string, WallPlaneDomainOperation[]>;
  readonly stairClippingOperations?: WallPlaneDomainOperation[];
  readonly stairClippingOpsBySubMassId?: ReadonlyMap<string, WallPlaneDomainOperation[]>;
}

export enum WallLOD {
  L1 = "L1", // base section layers
  L2 = "L2", // vertical section layers
}

interface WallLayerBase extends WallBase<WallType> {
  readonly id: string;
  readonly wallLayerType: WallLayerType;
}

// The order of these is important. Append only or revisit checks to wall kind.
export enum SolidWallKind {
  INTERIOR,
  EXTERIOR,
  NONENCLOSURE,
}

/**
 * A Solid Wall Mass is a base geometric representation of a section of
 * wall in the building. It can have internal WallAssemblyLayers associated with it which
 * represent more detailed subsections of the wall and also conform to the
 * SolidWallMass interface
 * ```
 *                                    +------------  Wall Mass
 *                            +-------|--------+
 *                            |       |        |
 *                            |+--------------+|
 *                            ||  +----------+||
 *                            ||  |          |||
 *                            ||  |          |||
 *           ------------------------------------------- CUTPLANE @ 48"
 *                            ||  |          |||
 *  Wall Substrate Layer -------+ |          |||
 *                            ||  |     -----------+ Wall Framing Layer
 *                            ||  |          |||
 *              INTERIOR      ||  |          |||
 *                            |+--------------+|
 *          +------------------------------+ ++|---+ Wall Substrate Layer
 *          |                 |            | |||
 *          | Floor Framing   |            | |||  INTERIOR
 *          |                 |            | |||
 *          +------------------------------+ ++|
 *                         +------------------+|
 *              EXTERIOR   |  |   |          |||
 *                         |  |   |          |||
 *                         |  |   |          |||
 *                         |  |   |          |||
 *           ------------------------------------------- CUTPLANE @ 18"
 *                         |  |   |          |||
 *  Wall Substrate Layer ---+ |   |     -----------+ Wall Framing Layer
 *                         |  |   |          |||
 *                         |  |   +----------+||
 *                         +------------------+|
 *                         +-----++------------------------------+
 *                         |  |  ||            |                 |
 *  Wall Substrate Layer +--- |  ||            |  Floor Framing  |
 *                         |  |  ||            |                 |
 *                         +-----++------------------------------+
 *                            +----------------+
 * ```
 */
export type SolidWallMass = WallBase<WallType.SOLID_MASS> & WallMassBase;
export type SolidWallLayer = SolidWallMass & WallLayerBase;

export interface SolidWallSide {
  readonly doors: GeneratedDoorPlacement[];
  readonly windows: GeneratedWindowPlacement[];
  readonly wallCuts: WallCut[];
  readonly finishes: SolidWallMass[];
  readonly descriptor?: WallSideDescriptor;
}

export type VoidWall = WallBase<WallType.VOID>;
export type VoidWallLayer = VoidWall & WallLayerBase;

/**
 * A SolidWall is a SolidWallMass that has had openings applied to it, and
 * has computed its wallFaces for rendering and splitting purposes.
 */
export interface SolidWall extends DynamicBuildingEntityBase<"WALL"> {
  readonly wallType: WallType.SOLID;
  readonly wallKind: SolidWallKind;
  readonly mass: Readonly<SolidWallMass>;
  readonly doorPlacements: GeneratedDoorPlacement[];
  readonly windowPlacements: GeneratedWindowPlacement[];
  readonly wallFaces: WallFaces;
  readonly wallOutlineFaces: WallFaces; // Wall faces without openings for building outline computation
  readonly wallCondition: WallCondition;
  readonly leftSide: SolidWallSide;
  readonly rightSide: SolidWallSide;

  /**
   * This mutable field should not be stored here as it prevents
   * caching
   */
  trimmedByStairs: boolean;
}

/**
 * Wall types used throughout the system as the base for any wall related calculations
 */
export type Wall = SolidWall | SolidWallMass | VoidWall;
export type WallMass = SolidWallMass | VoidWall;

/**
 * Wall End information for looking at walls with different offsets on either end
 */
export interface HalfEdgeWallEnd extends WallEndCap {
  readonly leftEdge: PolyLine2;
  readonly rightEdge: PolyLine2;
}
export interface PolyLine2ById {
  [id: string]: PolyLine2;
}
